import { useEffect, useState } from 'react';
import { DEFAULT_TAG_COLOUR, Size, TAG_NOTE_MAX_LENGTH, t, type DrawnTag, type PagedRecord, type TagCategoryView, type TagColour } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Select, TextInput, Textarea } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import { PAGES_PATH, apiFetch, post } from './api';
import { TagPill, TagSwatchPicker } from './TagSwatch';
import styles from './RecordPage.module.css';
import { reportCaughtInBrowser } from '../../shell/systemErrors';

/**
 * The Flags tile's + Add Tag (piece 13, decisions 0186, 0226): existing labels
 * are offered first and a new one is created on save. A tag is a label a user
 * creates, never a seeded list, and it gates no access.
 *
 * 🔨 The colour is offered only where the label is new (piece 13). Choosing a
 * label already in use applies that label's own colour, so one label is one
 * colour everywhere and a record page cannot fork it.
 *
 * 🔒 A new label asks its category first (decision 2260; screen C): choosing
 * one stands the swatches down, because the pill takes the category's color,
 * and No category brings them back. The note is this record's own (2261).
 */
export interface TagModalProps {
  open: boolean;
  recordType: PagedRecord;
  recordId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
  openerId?: string | null;
}

interface TagSuggestion {
  id: string;
  label: string;
  colour?: string | null;
  normalisedLabel?: string;
  /** 🔒 The label's own category, in use or not (decision 2260); absent from an older reply. */
  category?: TagCategoryView | null;
}

export function TagModal({ open, recordType, recordId, apiBaseUrl = '', onCancel, onSaved, openerId = null }: TagModalProps) {
  const [label, setLabel] = useState('');
  const [colour, setColour] = useState<TagColour>(DEFAULT_TAG_COLOUR);
  /** 🔒 The new label's category, or '' for No category (decision 2260). */
  const [categoryId, setCategoryId] = useState('');
  /** 🔒 The note on this record's tag (decision 2261). */
  const [note, setNote] = useState('');
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [categories, setCategories] = useState<TagCategoryView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const typed = label.trim();

  // A closed picker or an empty field offers nothing: derived at render, never written back by the effect.
  const offered = open && typed.length > 0 ? suggestions : [];
  // The label the person has typed, already in use. Its colour is the label's
  // own, so the picker stands down rather than offering a choice the save drops.
  const held = offered.find((row) => row.label.trim().toLocaleLowerCase() === typed.toLocaleLowerCase()) ?? null;
  /** The categories a new label can go into: those in use, in the Settings order. */
  const inUse = categories.filter((row) => row.isActive);
  const chosen = inUse.find((row) => row.id === categoryId) ?? null;

  useEffect(() => {
    if (!open || typed.length < 1) return undefined;
    const controller = new AbortController();
    apiFetch(`${apiBaseUrl}${PAGES_PATH}/tags?typed=${encodeURIComponent(typed)}`, { signal: controller.signal })
      .then((response) => (response.ok ? (response.json() as Promise<{ rows?: TagSuggestion[]; categories?: TagCategoryView[] }>) : { rows: [], categories: [] }))
      .then((body) => {
        if (controller.signal.aborted) return;
        setSuggestions(Array.isArray(body.rows) ? body.rows.slice(0, 8) : []);
        // 🔒 The categories ride on the same read (decision 2260).
        if (Array.isArray(body.categories)) setCategories(body.categories);
      })
      .catch((failed: unknown) => {
        reportCaughtInBrowser('records.tagSuggestions.read', failed);
      });
    return () => controller.abort();
  }, [open, typed, apiBaseUrl]);

  const save = async (body: { tagId?: string; label?: string; colour?: string | null; categoryId?: string | null; note?: string | null }) => {
    setError(null);
    // 🔨 A blank note is left out, so adding a tag the record already carries never clears the note it has (decision 2261).
    const answer = await post(`${PAGES_PATH}/tags/${recordType}/${recordId}`, { ...body, ...(note.trim() ? { note } : {}) }, apiBaseUrl);
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    setLabel('');
    setColour(DEFAULT_TAG_COLOUR);
    setCategoryId('');
    setNote('');
    onSaved();
    onCancel();
  };
  /** The new label as it will draw: the chosen category's color and icon, or its own swatch with none. */
  const preview: DrawnTag = held ? { id: held.id, label: held.label, colour: held.colour ?? null, category: held.category ?? null } : { id: 'new', label: typed, colour: chosen ? null : colour, category: chosen };

  return (
    <Modal
      open={open}
      title={t('tagModal.title')}
      subtitle={t('tagModal.subtitle')}
      onCancel={onCancel}
      openerId={openerId}
      dataModal="add-tag"
      actions={[
        { kind: 'primary', label: t('tagModal.save'), onAct: () => void save(held ? { tagId: held.id } : chosen ? { label: typed, categoryId: chosen.id } : { label: typed, colour }), disabled: typed.length === 0 || note.trim().length > TAG_NOTE_MAX_LENGTH },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <div data-tag-modal>
        {error && <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} />}
        <TextInput size={Size.Medium} label={t('tagModal.label')} value={label} onChange={setLabel} required />
        {/* 🔒 A new label asks its category; an existing one keeps its own category and color (decision 2260). */}
        {typed.length > 0 && (
          held
            ? <p className={styles.hint} data-tag-colour-reused={held.id}>{t('tagModal.reusedColour')}</p>
            : (
              <>
                <Select size={Size.Medium} label={t('tagModal.category')} value={categoryId} onChange={setCategoryId} options={[{ value: '', label: t('tag.noCategory') }, ...inUse.map((row) => ({ value: row.id, label: row.name }))]} />
                {chosen
                  ? <p className={styles.hint} data-tag-from-category={chosen.id}>{t('tagModal.fromCategory')}</p>
                  : <TagSwatchPicker name="tag-colour" value={colour} onChange={setColour} legend={t('tag.colour.legend')} />}
              </>
            )
        )}
        {typed.length > 0 && (
          <div className={styles.pills} data-tag-preview>
            <span>{t('tagModal.preview')}</span>
            <TagPill tag={preview} />
          </div>
        )}
        {/* 🔒 The note belongs to this record's tag, never to the shared label (decision 2261). */}
        {typed.length > 0 && (
          <>
            <Textarea size={Size.Medium} label={t('tagModal.note')} value={note} onChange={setNote} />
            <p className={styles.hint}>{t('tagModal.noteHint')}</p>
          </>
        )}
        {offered.length > 0 && (
          <div className={styles.pills} data-tag-suggestions>
            <span>{t('tagModal.suggestions')}</span>
            {offered.map((row) => (
              <button key={row.id} type="button" onClick={() => void save({ tagId: row.id })} data-tag-suggestion={row.id}>
                {row.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
